/**
 * React Query hooks for `api/skills` + `api/agents/:agentId/skills`.
 *
 * The previous `useAgentKnowledge` / `useAttachKnowledge` /
 * `useDetachKnowledge` hooks targeted the legacy
 * `/api/agents/:agentId/knowledge` controller that PR-A.1 removed. They
 * are replaced by the resource-scoped hooks under
 * `apps/dashboard/src/knowledge/api/agent-knowledge.ts` and the
 * `AgentEditorPage` callsite is rewired accordingly.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "./client";
import type { AgentSkillResponse, ListResponse, SkillResponse } from "./types";

export const skillsKeys = {
  all: (slug: string) => ["skills", slug] as const,
  list: (slug: string) => ["skills", slug, "list"] as const,
  agentSkills: (slug: string, agentId: string) => ["skills", slug, "agent", agentId] as const,
};

export function useSkills(slug: string) {
  return useQuery({
    queryKey: skillsKeys.list(slug),
    queryFn: () => apiRequest<ListResponse<SkillResponse>>("GET", "/api/skills"),
  });
}

export function useCreateSkill(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      type: SkillResponse["type"];
      description: string;
      inputSchema: Record<string, unknown>;
      configJsonb?: Record<string, unknown>;
      targetAgentId?: string | null;
    }) => apiRequest<SkillResponse>("POST", "/api/skills", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillsKeys.list(slug) });
    },
  });
}

export function useArchiveSkill(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>("DELETE", `/api/skills/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillsKeys.list(slug) });
    },
  });
}

export function useAgentSkills(slug: string, agentId: string | undefined) {
  return useQuery({
    queryKey: skillsKeys.agentSkills(slug, agentId ?? ""),
    queryFn: () =>
      apiRequest<ListResponse<AgentSkillResponse>>("GET", `/api/agents/${agentId}/skills`),
    enabled: Boolean(agentId),
  });
}

export function useAttachSkill(slug: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { skillId: string; configOverrideJsonb?: Record<string, unknown> }) =>
      apiRequest<AgentSkillResponse>("POST", `/api/agents/${agentId}/skills`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillsKeys.agentSkills(slug, agentId) });
    },
  });
}

export function useDetachSkill(slug: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) =>
      apiRequest<void>("DELETE", `/api/agents/${agentId}/skills/${skillId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillsKeys.agentSkills(slug, agentId) });
    },
  });
}
